// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY (tweet-archive-plan.md section 4, 12): an archived X/Twitter post's embed (type x_post) as the parts the
// browser draws: replied-to, main, quoted, each with its text and media. The children carry one text child per part
// (title = role) followed by its media children. Media is drawn as attachments (the attachment mosaic): a media item
// of a post X marks sensitive carries the spoiler flag, so it shows Fluxer's spoiler (tap to reveal), the same as a
// spoiler attachment, per item; the post's text stays visible. Never nsfw or the explicit-media flag: nothing is
// blurred or age-gated. Obyrus draws the same (embed_x_post.dart). Exclude from upstream sync.

import {
	EmbedMediaFlags,
	MessageAttachmentFlags,
	XPostEmbedChildTypes,
	type XPostRole,
	XPostRoles,
} from '@fluxer/constants/src/ChannelConstants';
import type {EmbedMedia, MessageEmbed} from '@fluxer/schema/src/domains/message/EmbedSchemas';
import type {MessageAttachment} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';

type MessageEmbedChild = NonNullable<MessageEmbed['children']>[number];

export interface XPostMediaItem {
	kind: 'image' | 'video';
	media: EmbedMedia;
	/** A video's poster image. */
	poster: EmbedMedia | undefined;
	animated: boolean;
	/** X marks the post sensitive: shown behind the spoiler. */
	spoiler: boolean;
}

export interface XPostPart {
	role: XPostRole;
	text: string;
	media: Array<XPostMediaItem>;
}

const ROLE_ORDER: ReadonlyArray<XPostRole> = [XPostRoles.REPLIED, XPostRoles.MAIN, XPostRoles.QUOTED];

function isRole(value: unknown): value is XPostRole {
	return value === XPostRoles.REPLIED || value === XPostRoles.MAIN || value === XPostRoles.QUOTED;
}

/** Never nsfw or explicit (no blur, no age gate); the spoiler flag is kept. */
export function unmarkedMedia(media: EmbedMedia): EmbedMedia {
	return {...media, nsfw: false, flags: (media.flags ?? 0) & ~EmbedMediaFlags.CONTAINS_EXPLICIT_MEDIA};
}

const isSpoiler = (media: EmbedMedia | null | undefined) => ((media?.flags ?? 0) & EmbedMediaFlags.IS_SPOILER) !== 0;

export function xPostParts(embed: Pick<MessageEmbed, 'children'>): Array<XPostPart> {
	const byRole = new Map<XPostRole, XPostPart>();
	const partOf = (role: XPostRole) => {
		let part = byRole.get(role);
		if (!part) {
			part = {role, text: '', media: []};
			byRole.set(role, part);
		}
		return part;
	};
	for (const child of (embed.children ?? []) as ReadonlyArray<MessageEmbedChild>) {
		if (!isRole(child.title)) continue;
		if (child.type === XPostEmbedChildTypes.TEXT) {
			partOf(child.title).text = child.description ?? '';
		} else if (child.type === XPostEmbedChildTypes.MEDIA) {
			if (child.video?.url) {
				partOf(child.title).media.push({
					kind: 'video',
					media: unmarkedMedia(child.video),
					poster: child.thumbnail ? unmarkedMedia(child.thumbnail) : undefined,
					animated: ((child.video.flags ?? 0) & EmbedMediaFlags.IS_ANIMATED) !== 0,
					spoiler: isSpoiler(child.video),
				});
			} else if (child.image?.url) {
				partOf(child.title).media.push({
					kind: 'image',
					media: unmarkedMedia(child.image),
					poster: undefined,
					animated: false,
					spoiler: isSpoiler(child.image),
				});
			}
		}
	}
	return ROLE_ORDER.flatMap((role) => {
		const part = byRole.get(role);
		return part && (part.text.length > 0 || part.media.length > 0) ? [part] : [];
	});
}

function filenameOf(url: string): string {
	try {
		return decodeURIComponent(new URL(url).pathname.split('/').pop() || 'media');
	} catch {
		return 'media';
	}
}

/**
 * A part's media as the attachments the attachment mosaic draws: photos, videos and GIFs (a video with the animated
 * flag); a sensitive post's items with the spoiler flag, so each is behind Fluxer's spoiler as a spoiler attachment.
 */
export function partAttachments(part: XPostPart, key: string): Array<MessageAttachment> {
	return part.media.flatMap((item, index) => {
		const media = item.media;
		if (!media.url || !media.proxy_url) return [];
		const flags =
			(item.spoiler ? MessageAttachmentFlags.IS_SPOILER : 0) | (item.animated ? MessageAttachmentFlags.IS_ANIMATED : 0);
		return [
			{
				id: `${key}-${part.role}-${index}`,
				filename: filenameOf(media.url),
				title: undefined,
				description: undefined,
				content_type: media.content_type ?? (item.kind === 'video' ? 'video/mp4' : 'image/jpeg'),
				size: 0,
				url: media.url,
				proxy_url: media.proxy_url,
				width: media.width ?? undefined,
				height: media.height ?? undefined,
				placeholder: media.placeholder ?? undefined,
				placeholder_version: undefined,
				flags,
				duration: media.duration ?? undefined,
				waveform: undefined,
				content_hash: media.content_hash ?? undefined,
				nsfw: false,
			} as MessageAttachment,
		];
	});
}
